/**
 * WidgetChannel Module
 * Handles connection to the WidgetChannel for widget client communication
 */
class WidgetChannel {
  /**
   * Create a new WidgetChannel module
   * @param {Object} coreService - The core WebSocket service
   */
  constructor(coreService) {
    this.core = coreService;
    this.widgetSubscription = null;
    this.handlers = {};
  }

  /**
   * Connect to the WidgetChannel
   * @param {string} sessionId - The widget session ID
   * @param {Object} handlers - Event handlers for the widget channel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect(sessionId, handlers = {}) {
    if (!this.core.initialized) {
      this.core.initialize();
    }
    
    // Disconnect any existing widget subscription
    this.disconnect();
    
    // Validate session ID
    if (!sessionId) {
      console.error('Session ID is required to connect widget client');
      return null;
    }
    
    // Store handlers
    this.handlers = handlers || {};
    
    console.log(`Connecting widget WebSocket for session: ${sessionId}`);
    
    try {
      // Create subscription
      this.widgetSubscription = this.core.cable.subscriptions.create(
        {
          channel: 'WidgetChannel',
          session_id: sessionId
        },
        {
          connected: () => {
            console.log(`Widget WebSocket connected for session: ${sessionId}`);
            
            if (this.handlers.onConnected) {
              this.handlers.onConnected();
            }
          },
          
          disconnected: () => {
            console.log(`Widget WebSocket disconnected for session: ${sessionId}`);
            
            if (this.handlers.onDisconnected) {
              this.handlers.onDisconnected();
            }
          },
          
          received: (data) => {
            console.log(`Widget received message:`, data);
            
            this.handleWidgetMessage(data);
          },
          
          // Custom actions
          sendMessage: function(content, messageType = 'text') {
            this.perform('send_message', { content, message_type: messageType });
          },
          
          ping: function() {
            this.perform('ping', { timestamp: new Date().toISOString() });
          },
          
          requestTakeover: function(reason) {
            this.perform('request_takeover', { reason });
          }
        }
      );
      
      return this.widgetSubscription;
    } catch (error) {
      console.error('Error connecting to WidgetChannel:', error);
      return null;
    }
  }

  /**
   * Disconnect from the WidgetChannel
   */
  disconnect() {
    if (this.widgetSubscription) {
      try {
        this.core.cable.subscriptions.remove(this.widgetSubscription);
      } catch (error) {
        console.warn('Error disconnecting from WidgetChannel:', error);
      }
      this.widgetSubscription = null;
    }
  }

  /**
   * Handle incoming widget messages
   * @param {Object} data - The message data
   */
  handleWidgetMessage(data) {
    if (!data || !data.action) {
      console.warn('Received widget message without action:', data);
      return;
    }
    
    switch (data.action) {
      case 'new_message':
        if (this.handlers.onNewMessage) {
          this.handlers.onNewMessage(data.message);
        }
        break;
        
      case 'takeover_requested':
        if (this.handlers.onTakeoverRequested) {
          this.handlers.onTakeoverRequested(data.takeover_id, data.message);
        }
        break;
        
      case 'takeover_accepted':
        if (this.handlers.onTakeoverAccepted) {
          this.handlers.onTakeoverAccepted(data.attendant_name, data.message);
        }
        break;
        
      case 'takeover_completed':
        if (this.handlers.onTakeoverCompleted) {
          this.handlers.onTakeoverCompleted(data.returned_to_bot, data.message);
        }
        break;
        
      case 'takeover_unavailable':
        if (this.handlers.onTakeoverUnavailable) {
          this.handlers.onTakeoverUnavailable(data.offHours, data.message);
        }
        break;
        
      default:
        console.log(`Unhandled widget message action: ${data.action}`, data);
        if (this.handlers.onUnhandledMessage) {
          this.handlers.onUnhandledMessage(data);
        }
    }
  }

  /**
   * Send a message
   * @param {string} content - The message content
   * @param {string} messageType - The message type (default: 'text')
   * @returns {boolean} Whether the message was sent successfully
   */
  sendMessage(content, messageType = 'text') {
    if (this.widgetSubscription) {
      try {
        this.widgetSubscription.sendMessage(content, messageType);
        return true;
      } catch (error) {
        console.error('Error sending widget message:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Send a ping
   * @returns {boolean} Whether the ping was sent successfully
   */
  sendPing() {
    if (this.widgetSubscription) {
      try {
        this.widgetSubscription.ping();
        return true;
      } catch (error) {
        console.error('Error sending widget ping:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Request a takeover
   * @param {string} reason - The reason for the takeover
   * @returns {boolean} Whether the request was sent successfully
   */
  requestTakeover(reason) {
    if (this.widgetSubscription) {
      try {
        this.widgetSubscription.requestTakeover(reason);
        return true;
      } catch (error) {
        console.error('Error requesting takeover:', error);
        return false;
      }
    }
    return false;
  }
}

export default WidgetChannel;
